<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <div :class="containerClass">
            <div>
                <b-alert
                    variant="light"
                    :show="showInstruction"
                >
                    <div class="alert-body" v-html="instruction"/>
                </b-alert>
            </div>
            <Transition mode="out-in" name="fade">
                <div class="pb-2" v-if="showInformationOwnerSettings">
                    <b-form-checkbox
                        v-model="weHaveInformation"
                        @change="weHaveInformationChanged"
                        :disabled="isWeHaveInformationCheckboxDisabled"
                    >
                        {{ $t('Page(ProductCard-ConcernElements):Component(Radio):text-has-information') }}
                    </b-form-checkbox>
                    <b-form-checkbox
                        v-model="supplierHasInformation"
                        @change="supplierHasInformationChanged"
                    >
                        {{ $t('Page(ProductCard-ConcernElements):Component(Radio):text-supplier-information') }}
                    </b-form-checkbox>
                </div>
            </Transition>
            <Transition mode="out-in" name="fade">
                <div v-show="isContentVisible">
                    <div>
                        <app-collapse
                            type="border"
                            class="mb-1"
                        >
                            <app-collapse-item
                                v-for="list in substanceLists"
                                :ref="'collapse-' + list.id"
                                title=""
                                card-body-class="p-0"
                                :class="{'color-default-important': false !== list.questionAnswer}"
                                :is-visible="autoExpandLists || expandLists"
                                :no-animation="isLargeList(list)"
                                @visible="onListVisibilityChange(list.id, $event)"
                            >
                                <template #header>
                                    <div class="lead collapse-title">
                                        <template v-if="isRootBadgeReferenceTitleMode(list)">
                                            {{ list.title }}
                                            <b-badge
                                                v-for="rootBadge in list.referenceRootBadges"
                                                :key="rootBadge.id"
                                                class="ml-50"
                                                variant="light-primary"
                                                :title="rootBadge.title"
                                            >
                                                {{ rootBadge.rootTitle }}
                                            </b-badge>
                                        </template>
                                        <span v-else-if="list.hasReferencedLists">
                                            {{ list.referencedLists.join(' & ') }} /
                                        </span>
                                        <template v-if="!isRootBadgeReferenceTitleMode(list)">
                                            {{ list.title }}
                                        </template>
                                        <InstructionInfoButton
                                            v-if="legislationTranslationsLoaded && hasSubstanceListTranslation(list)"
                                            custom-class="popover-wide"
                                            class="ml-1"
                                            :button="false"
                                            :instruction="getSubstanceListTranslation(list)"
                                        />
                                    </div>
                                    <div
                                        v-if="showQuestions && list.hasQuestion"
                                        class="d-flex justify-content-end align-items-center"
                                    >
                                        <div class="pr-1">{{ list.question }}</div>
                                        <div class="demo-inline-spacing" @click.stop>
                                            <b-form-radio
                                                class="mt-0"
                                                :name="'question-for-' + list.id"
                                                :value="true"
                                                v-model="list.questionAnswer"
                                                @change="questionAnswered(list, true, true)"
                                            >
                                                {{ $t('Component(Choice):yes') }}
                                            </b-form-radio>
                                            <b-form-radio
                                                class="mt-0"
                                                :name="'question-for-' + list.id"
                                                :value="false"
                                                :disabled="list.noQuestionDisabled"
                                                v-model="list.questionAnswer"
                                                @change="questionAnswered(list, false)"
                                            >
                                                {{ $t('Component(Choice):no') }}
                                            </b-form-radio>
                                        </div>
                                    </div>
                                </template>
                                <InstructionText
                                    class="m-1"
                                    v-if="showSubstanceListInstruction && isListInstructionPresented(list.key)"
                                >
                                    {{ listInstruction(list.key) }}
                                </InstructionText>

                                <div v-if="showNoSinglePartsCheckbox && isNoSinglePartsCheckboxVisible(list)">
                                    <b-alert
                                        class="m-1"
                                        variant="light"
                                        show
                                    >
                                        <div class="alert-body">
                                            {{ $t('Component(ConcernElements):instruction-checkbox') }}
                                        </div>
                                    </b-alert>
                                    <div class="text-right pb-1">
                                        <b-form-checkbox
                                            inline
                                            :name="'excluded-control-' + list.key"
                                            class="custom-control-success"
                                            @input="setAsNotIncluded(list)"
                                        >
                                            {{ $t('Page(ProductCard-ConcernElements):Component(Text):no-single-part-contains-above-' + list.key) }}
                                        </b-form-checkbox>
                                    </div>
                                </div>

                                <SubstanceTableSearchOnlyV1
                                    v-if="isListTableRendered(list.id) && isSearchOnlyMode(list)"
                                    :list="list"
                                    :included-substances="includedSubstances"
                                    :excluded-substances="excludedSubstances"
                                    :read-only="readOnly"
                                    :read-only-for-included="readOnlyForIncluded"
                                    :included-radio-disabled="includedRadioDisabled"
                                    :included-radio-title="includedRadioTitle"
                                    @include="s => include(s, list)"
                                    @exclude="s => exclude(s, list)"
                                    @undefined="tryMakeUndefined"
                                    @includeSubstanceToAList="includeSubstanceToAList"
                                />

                                <SubstanceTable
                                    v-if="isListTableRendered(list.id) && !isSearchOnlyMode(list)"
                                    :list="list"
                                    :list-id="list.id"
                                    :is-list-candidate-list="isCandidateList(list)"
                                    :substances="list.substances"
                                    :included-substances="includedSubstances"
                                    :excluded-substances="excludedSubstances"
                                    :auto-expand-substances="autoExpandSubstances"
                                    :read-only="readOnly"
                                    :read-only-for-included="readOnlyForIncluded"
                                    :included-radio-disabled="includedRadioDisabled"
                                    :included-radio-title="includedRadioTitle"
                                    @include="s => include(s, list)"
                                    @exclude="s => exclude(s, list)"
                                    @undefined="tryMakeUndefined"
                                    @includeSubstanceToAList="includeSubstanceToAList"
                                />
                            </app-collapse-item>
                        </app-collapse>
                    </div>
                </div>
            </Transition>
        </div>
    </b-overlay>
</template>

<script>
import { BAlert, BBadge, BFormRadio, BFormCheckbox, BTable, BFormGroup, BFormRadioGroup, BButton, BOverlay } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import {SubstanceApi} from "@core/api";
import {NestedTable} from "@core/components/common";
import dateRenderer from "@core/mixins/date-renderer";
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import {getAllSubstanceLists, getSubstanceList} from "@core/domain/substance";
import Vue from 'vue'
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import InstructionInfoButton from "@core/components/common/instructions/InstructionInfoButton.vue";
import SubstanceTable from "@/views/product/components/product-card/SubstanceTableV5.vue";
import SubstanceTableSearchOnlyV1 from "@/views/product/components/product-card/SubstanceTableSearchOnlyV1.vue";
import ActiveSubstanceListsMixin from "@/views/product/components/product-card/ActiveSubstanceListsMixin";
import SubstanceListReferenceGraphMixin from "@/views/product/components/product-card/SubstanceListReferenceGraphMixin";
import {SubstanceListRenderMode, SubstanceLists} from "@/libs/substance";
import legislationTranslations from "@core/mixins/legislation-translations";

export default {
    mixins: [dateRenderer, ActiveSubstanceListsMixin, SubstanceListReferenceGraphMixin, legislationTranslations],
    components: {
        InstructionInfoButton, InstructionText, SubstanceTable, SubstanceTableSearchOnlyV1,
        BBadge, BButton, BFormCheckbox, BOverlay, BAlert, BFormRadioGroup, vSelect, BFormGroup, BTable, BFormRadio, NestedTable,
        AppCollapse, AppCollapseItem
    },
    directives: {Ripple},
    props: {
        // Explicit opt-in from ProductCardV5; request workflows retain legacy context.
        productStatementAnswers: { type: Array, default: null },
        product: {
            type: Object,
            default() {
                return {
                    substanceListQuestionStates: [],
                    informationOwnerSettings: {
                        weHaveInformation: null
                    }
                };
            }
        },
        instruction: {
            default() {
                return this.$t('Page(ProductCard-ConcernElements):Component(Text):hint');
            }
        },
        showInformationOwnerSettings: {
            default: true,
        },
        companyHasInformation: {
            default: false
        },
        includedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        excludedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        legislations: {
            default() {
                return null;
            }
        },
        allowedListIds: {
            type: Array,
            default: null,
        },
        showInstruction: {
            default: true
        },
        showSubstanceListInstruction: {
            default: true
        },
        showNoSinglePartsCheckbox: {
            default: true,
        },
        disableAutomaticQuestionStateInference: {
            type: Boolean,
            default: false,
        },
        showQuestions: {
            default: true
        },
        autoExpandLists: {
            default: false
        },
        autoExpandListWhenOnlyOne: {
            default: true
        },
        autoExpandSubstances: {
            default: false
        },
        containerClass: {
            default: 'p-1'
        },
        readOnly: {
            default: false
        },
        readOnlyForIncluded: {
            default: false
        },
        includedRadioDisabled: {
            default: false
        },
        includedRadioTitle: {
            default: null
        },
        automaticallySyncIncludedSubstances: {
            default: false
        },
        referenceTitleMode: {
            type: String,
            // default: 'root-badges',
            default: 'path-prefix',
        }
    },
    data() {
        return {
            showLoading: true,
            notContainsAboveSpecifiedLevelCheckbox: false,

            expandLists: false,
            substanceLists: [],
            allSubstanceLists: [],

            weHaveInformation: null,
            supplierHasInformation: null,

            selectedSubstances: {},

            substances: [],
            loadedSubstances: {},
            renderedListIds: {},

            areListsLoaded: false,
            fetchSubstanceListsPromise: null,
        };
    },

    methods: {
        isLargeList(list) {
            return Array.isArray(list.substances) && list.substances.length >= 1000;
        },
        isSearchOnlyMode(list) {
            return (list.renderMode || SubstanceListRenderMode.DEFAULT) === SubstanceListRenderMode.SEARCH_ONLY;
        },
        isContainerOnlyMode(list) {
            return (list.renderMode || SubstanceListRenderMode.DEFAULT) === SubstanceListRenderMode.CONTAINER_ONLY;
        },
        onListVisibilityChange(listId, visible) {
            if (visible) {
                this.$set(this.renderedListIds, listId, true);
            }
        },
        isListTableRendered(listId) {
            return this.renderedListIds[listId] || this.autoExpandLists || this.expandLists;
        },
        isCandidateList(list) {
            return SubstanceLists.CANDIDATE_LIST === list.key;
        },
        isRootBadgeReferenceTitleMode(list) {
            return this.referenceTitleMode === 'root-badges'
                && list.hasReferencedLists
                && list.referenceRootBadges.length > 0;
        },
        buildReferenceRootBadges(paths, list) {
            const badges = {};

            paths.forEach(path => {
                if (!path.length) {
                    return;
                }

                const root = path[0];
                const rootId = root.getId();
                const fullPath = path.concat([list]).map(l => l.title).join(' / ');

                if (!badges[rootId] || badges[rootId].pathLength < path.length) {
                    badges[rootId] = {
                        id: rootId,
                        rootTitle: root.title,
                        title: fullPath,
                        pathLength: path.length,
                    };
                }
            });

            return Object.values(badges);
        },
        questionAnswered(list, answer, expand = false) {
            if (list.questionAnswer !== answer) {
                list.questionAnswer = answer;
            }

            if (answer) {
                list.disabled = false;
                if (expand) {
                    this.$nextTick(() => {
                        this.expandListSubstances(list);
                    });
                }
            } else {
                list.disabled = true;

                //  update collapse
                if (answer === false) {
                    this.collapseListSubstances(list);
                }
            }

            if (this.isListInitialized(list) && list.hasQuestion) {
                this.$emit('listQuestionStateUpdated', {id: list.id, title: list.title}, answer);

                if (false === answer) {
                    this.setAllAsExcluded(list);
                }
            }
        },
        expandListSubstances(list) {
            if (this.$refs['collapse-' + list.id] && this.$refs['collapse-' + list.id].length) {
                this.$refs['collapse-' + list.id][0].updateVisible(true);
            }
        },
        collapseListSubstances(list) {
            if (this.$refs['collapse-' + list.id] && this.$refs['collapse-' + list.id].length) {
                this.$refs['collapse-' + list.id][0].updateVisible(false);
            }
        },
        updateStateForAllLists() {
            this.substanceLists.map(l => this.updateListState(l));
        },
        isListInitialized(list) {
            return list.hasOwnProperty('initialized');
        },
        updateListState(list) {
            if (!this.showQuestions) {
                return;
            }
            //  list can be
            //   - with or without question
            //   - enabled or disabled
            //   - answered or not
            const hasQuestion = list.domain.hasQuestion();

            if (!hasQuestion) {
                list.disabled = false;
            } else if (true !== list.questionAnswer) {
                list.disabled = true;
            }

            if (this.areListsLoaded && hasQuestion) {
                if (!this.isListInitialized(list)) {
                    //  initialize saved state
                    const existingState = this.product.substanceListQuestionStates.find(s => s.list === list.id);
                    // const existingState = this.substanceListQuestionStates.find(s => s.list === list.id);
                    if (existingState) {
                        list.questionAnswer = existingState.state;
                        this.questionAnswered(list, existingState.state);
                    }
                }

                if (this.disableAutomaticQuestionStateInference) {
                    list.initialized = true;
                    return;
                }

                if (!list.hasOwnProperty('checkingSubstances')) {
                    list.checkingSubstances = false;
                }

                if (this.includedSubstances.length) {
                    //  initialize calculated state for included CE
                    list.checkingSubstances = true;
                    list.domain
                        .extractIncluded(this.includedSubstances.map(c => c.substance))
                        .then(included => {
                            this.inActivateNoChoice(list, included.length > 0)
                            if (included.length > 0) {
                                this.questionAnswered(list, true);

                                if (!this.isListInitialized(list)) {
                                    // this.$nextTick(() => {
                                    //     this.expandListSubstances(list)
                                    // });
                                }
                            } else {
                                this.tryToCheckNoAnswer(list);
                            }
                            list.initialized = true;
                        })
                        .finally(() => list.checkingSubstances = false)
                } else {
                    list.initialized = true;
                    this.inActivateNoChoice(list, false);
                    this.tryToUncheckYesAnswer(list);
                    this.tryToCheckNoAnswer(list);
                    this.tryToUncheckNoAnswerInCaseThereAreUndefinedSubstances(list);
                }
            }
        },

        /**
         * make the choice "no" disabled -> product does not contain substances are inactivated
         * @param list
         * @param state
         */
        inActivateNoChoice(list, state) {
            list.noQuestionDisabled = state;
        },
        weHaveInformationChanged() {
            this.emitInformationOwnerSettingsChanged();
        },
        supplierHasInformationChanged() {
            this.emitInformationOwnerSettingsChanged();
        },
        tryMakeUndefined(substance) {
            if (this.isIncluded(substance) || this.isExcluded(substance)) {
                substance.selected = null;
                this.$emit('undefined', substance);
            }
        },
        includeSubstanceToAList(substance, parent, list, rerenderTable) {
            if (parent && parent.type === 'list-reference') {
                const referencedList = this.allSubstanceLists.find(l => l.id === parent.id);
                if (referencedList) {
                    referencedList.includeSubstance(substance, null).then(() => {
                        list.domain.includedIntoReferencedList(substance, parent.id);
                        const visibleSubstanceList = this.substanceLists.find(l => l.id === referencedList.id);
                        if (visibleSubstanceList) {
                            visibleSubstanceList.substances.push(substance);
                        }
                        rerenderTable();
                    });
                }
            } else {
                list.domain.includeSubstance(substance, parent).then(() => {
                    const visibleSubstanceList = this.substanceLists.find(l => l.id === list.id);
                    if (visibleSubstanceList) {
                        visibleSubstanceList.substances.push(substance);
                    }
                    rerenderTable();
                });
            }
        },
        include(substance, list = null) {
            this.notContainsAboveSpecifiedLevelCheckbox = false;
            const activeListIds = this.substanceLists.map(l => l.id);
            const referencedListIds = [];
            const productContext = this.buildSubstanceListVisibilityProductContext();

            this.substanceLists.forEach(activeList => {
                const referenced = this.substanceListReferenceGraph && activeList.domain
                    ? this.substanceListReferenceGraph.getTransitiveReferencedListIds(activeList.domain, productContext)
                    : [];
                referenced.forEach(id => referencedListIds.push(id));
            });

            const onlyUnique = (value, index, array) => {
                return array.indexOf(value) === index;
            }
            const includeListIds = (list && list.id ? [list.id] : []).concat(activeListIds, referencedListIds);

            this.$emit('include', substance, includeListIds.filter(onlyUnique));
        },

        exclude(substance, list) {
            this.$emit('exclude', substance);
        },

        isIncluded(substance) {
            return this.includedSubstances.find(s => s.substance.id === substance.id);
        },

        isExcluded(substance) {
            return this.excludedSubstances.find(s => s.substance.id === substance.id);
        },

        initSelectedStatus(substance) {
            substance.selected = Vue.observable(null);
            if (this.isIncluded(substance)) {
                substance.selected = true;
            } else if (this.isExcluded(substance)) {
                substance.selected = false;
            }
        },

        initSelectedStatusRecursively(substance) {
            this.initSelectedStatus(substance);
            if (substance.hasOwnProperty('children')) {
                substance.children = substance.children.map(child => {
                    return {...child};
                });
                substance.children.forEach(this.initSelectedStatusRecursively);
            }
        },

        setAllAsExcluded(list) {
            this.setSubstancesAsExcluded(list.substances);
            this.$nextTick(() => {
                this.tryToCheckNoAnswer(list);
            });
        },

        setSubstancesAsExcluded(substances) {
            substances.map(s => {
                if (s.selected !== false) {
                    s.selected = false;
                    this.$emit('exclude', s);
                }

                if (s.hasOwnProperty('children')) {
                    this.setSubstancesAsExcluded(s.children);
                }
            });
        },

        tryToUncheckYesAnswer(list) {
            if (this.disableAutomaticQuestionStateInference) {
                return;
            }

            //  if question is different then yes -> do nothing
            if (list.questionAnswer !== true) {
                return;
            }
            //  in case there are no included substances, we can set to null
            if (this.includedSubstances.length === 0 && list.questionAnswer) {
                this.questionAnswered(list, null);
                return;
            }
            //  check if the list has included substances
            list.domain
                .extractIncluded(this.includedSubstances.map(c => c.substance))
                .then(included => {
                    if (included.length === 0) {
                        this.inActivateNoChoice(list, false)
                        this.questionAnswered(list, null);
                    }
                });
        },

        async tryToCheckNoAnswer(list) {
            if (this.disableAutomaticQuestionStateInference) {
                return;
            }

            if (false === list.questionAnswer) {
                return;
            }
            const excluded = await list.domain.extractSelectableIncluded(this.excludedSubstances.map(s => s.substance));
            const selectableCount = list.domain.getSelectableCount();
            if (selectableCount > 0 && excluded.length === selectableCount) {
                list.questionAnswer = false;
                this.questionAnswered(list, false);
            }
        },

        async tryToUncheckNoAnswerInCaseThereAreUndefinedSubstances(list) {
            if (this.disableAutomaticQuestionStateInference) {
                return;
            }

            if (false !== list.questionAnswer) {
                //  exit when answer is not defined or true
                return;
            }

            const excluded = await list.domain.extractSelectableIncluded(this.excludedSubstances.map(s => s.substance));
            if (excluded.length !== list.domain.getSelectableCount()) {
                this.questionAnswered(list, null);
            }
        },

        setAsNotIncluded(list) {
            this.setSubstancesAsNotIncluded(list.substances);
            this.$nextTick(() => {
                this.tryToCheckNoAnswer(list);
            });
        },

        setSubstancesAsNotIncluded(substances) {
            substances.map(s => {
                if (null === s.selected) {
                    s.selected = false;
                    this.$emit('exclude', s);
                }

                if (s.hasOwnProperty('children')) {
                    this.setSubstancesAsNotIncluded(s.children);
                }
            });
        },

        emitInformationOwnerSettingsChanged() {
            this.$emit('informationOwnerSettingsChanged', {
                weHaveInformation: this.weHaveInformation,
                supplierHasInformation: this.supplierHasInformation
            })
        },
        fetchSubstanceLists() {
            this.showLoading = true;
            if (null === this.legislations || this.legislations.length === 0) {
                return getAllSubstanceLists().finally(() => this.showLoading = false);
            }
            if (this.fetchSubstanceListsPromise) {
                return this.fetchSubstanceListsPromise;
            }
            return this.fetchSubstanceListsPromise = Promise
                .all([
                    getAllSubstanceLists(),
                    this.loadSubstanceListVisibilityCategories(),
                ])
                .then(([lists]) => {
                    this.allSubstanceLists = lists;
                    const productContext = this.buildSubstanceListVisibilityProductContext();
                    const graph = this.createSubstanceListReferenceGraph(lists);

                    return {
                        graph,
                        rootLists: graph.resolveRootLists(this.legislations, productContext),
                        lists: graph.resolveActiveLists(this.legislations, productContext),
                        productContext,
                    };
                })
                .then(({graph, rootLists, lists, productContext}) => {
                    const referencedListReferencingListsMap = graph.buildReferencedToParentPathsMap(rootLists, lists, productContext);
                    const allowedListIds = Array.isArray(this.allowedListIds)
                        ? this.allowedListIds.map(id => String(id))
                        : null;
                    const renderableLists = lists
                        .filter(list => !this.isContainerOnlyMode(list))
                        .filter(list => (
                            allowedListIds === null
                            || allowedListIds.includes(String(list.id))
                        ));

                    //  end of building the map
                    return Promise.all(renderableLists.map(list => {
                        return new Promise((resolve, reject) => {
                            const dto = list.toDTO();
                            dto.hasQuestion = list.hasQuestion();
                            dto.question = list.question;
                            dto.questionAnswer = null;
                            dto.noQuestionDisabled = false;
                            dto.disabled = false;
                            //  manage the referenced lists
                            dto.hasReferencedLists = referencedListReferencingListsMap.hasOwnProperty(list.id);
                            if (dto.hasReferencedLists) {
                                dto.referencedLists = referencedListReferencingListsMap[list.id]
                                    .map(path => path.map(l => l.title).join(' / '));
                                dto.referenceRootBadges = this.buildReferenceRootBadges(
                                    referencedListReferencingListsMap[list.id],
                                    list,
                                );
                            } else {
                                dto.referenceRootBadges = [];
                            }
                            //  this reference is needed to be able to include substance to a list
                            dto.domain = list;  // I do not like it todo do it in another way

                            if (this.isSearchOnlyMode(dto)) {
                                dto.substances = [];
                                resolve(dto);
                                return;
                            }

                            list
                                .getSubstances()
                                .then(substances => {
                                    dto.substances = substances.map(s => {
                                        const substanceCopy = {...s};
                                        this.initSelectedStatusRecursively(substanceCopy);
                                        substanceCopy.configuration = {};
                                        substanceCopy.expanded = [];
                                        return substanceCopy;
                                    });

                                    resolve(dto);
                                })
                                .catch(reject)
                            ;
                        });
                    }));
                })
                .then(lists => {
                    this.substanceLists = lists;

                    this.areListsLoaded = true;

                    if (this.autoExpandListWhenOnlyOne && this.substanceLists.length === 1) {
                        this.expandLists = true;
                    }

                    this.calculateIncludedSubstancesSelectedState();
                    this.calculateExcludedSubstancesSelectedState();
                })
                .finally(() => {
                    this.$emit('loaded');
                    this.showLoading = false
                    this.fetchSubstanceListsPromise = null;
                })
            ;
        },
        refreshSubstanceListsForVisibilityContext() {
            if (!this.areListsLoaded || this.fetchSubstanceListsPromise) {
                return;
            }

            this.fetchSubstanceLists();
        },

        calculateIncludedSubstancesSelectedState() {
            const recursivelySetState = (substanceContainer, substances) => {
                substances.forEach(s => {
                    if (s.id === substanceContainer.substance.id) {
                        s.selected = true;
                    }
                    recursivelySetState(substanceContainer, s.children);
                });
            };

            this.includedSubstances.forEach((s) => {
                this.substanceLists.forEach(l => {
                    recursivelySetState(s, l.substances);
                });
            });

            if (this.automaticallySyncIncludedSubstances) {
                const recursivelySetUndefinedState = (definedIncludedSubstances, substances) => {
                    substances.forEach(s => {
                        if (s.selected && !definedIncludedSubstances.includes(s.id)) {
                            s.selected = null;
                        }
                        recursivelySetUndefinedState(definedIncludedSubstances, s.children);
                    });
                };
                const includedSubstanceIds = this.includedSubstances.map(substanceContainer => substanceContainer.substance.id);
                this.substanceLists.forEach(l => {
                    recursivelySetUndefinedState(includedSubstanceIds, l.substances);
                });
            }

            this.updateStateForAllLists();
        },

        calculateExcludedSubstancesSelectedState() {
            const idList = this.excludedSubstances.map((s) => s.substance.id);
            const recursivelySetState = (s) => {
                if (idList.includes(s.id)) {
                    s.selected = false;
                }
                s.children.forEach(recursivelySetState);
            };

            this.substanceLists.forEach(l => {
                l.substances.forEach(recursivelySetState);
            });

            this.updateStateForAllLists();
        },

        isNoSinglePartsCheckboxVisible(list) {
            return this.isTranslationSet(
                'Page(ProductCard-ConcernElements):Component(Text):no-single-part-contains-above-' + list.key
            );
        },

        hasSubstanceListTranslation(list) {
            if (this.hasLegislationSubstanceListInfoTranslation(list.id)) {
                return true;
            }

            return this.isTranslationSet('Component(IButton):ce-substance-list-instruction-' + list.key);
        },

        getSubstanceListTranslation(list) {
            const translation = this.getLegislationSubstanceListInfoTranslation(list.id);
            if (translation) {
                return translation;
            }

            return this.$t('Component(IButton):ce-substance-list-instruction-' + list.key);
        },

        isTranslationSet(key) {
            const translation = this.$t(key);

            return translation !== key && translation.length > 0;
        },

        isListInstructionPresented(listKey) {
            return this.isTranslationSet('Component(ConcernElements):substance-list-instruction-' + listKey);
        },

        listInstruction(listKey) {
            return this.$t('Component(ConcernElements):substance-list-instruction-' + listKey);
        }
    },

    computed: {
        isContentVisible() {
            if (this.includedSubstances.length > 0 || this.excludedSubstances.length > 0) {
                return true;
            }

            return this.weHaveInformation || this.companyHasInformation;
        },
        isWeHaveInformationCheckboxDisabled() {
            return this.includedSubstances.length > 0 || this.excludedSubstances.length > 0;
        },
        substanceListVisibilityContextKey() {
            const productContext = this.buildSubstanceListVisibilityProductContext();
            const normalizeIds = ids => (ids || [])
                .map(id => Number(id))
                .filter(id => Number.isFinite(id))
                .sort((a, b) => a - b);
            const normalizeStatements = statements => (statements || [])
                .map(statement => {
                    const statementValue = statement.statement && typeof statement.statement === 'object'
                        ? statement.statement.id
                        : statement.statement;
                    const statementId = Number(statement.id || statementValue);

                    return {
                        id: statementId,
                        coveredExcluded: statement.coveredExcluded,
                    };
                })
                .filter(statement => Number.isFinite(statement.id))
                .sort((a, b) => a.id - b.id);

            return JSON.stringify({
                materialCategoryIds: normalizeIds(productContext.materialCategoryIds),
                mixtureCategoryIds: normalizeIds(productContext.mixtureCategoryIds),
                regulatedProductStatements: normalizeStatements(productContext.regulatedProductStatements),
            });
        },
    },

    watch: {
        product() {
            if (this.product.informationOwnerSettings) {
                this.weHaveInformation = this.product.informationOwnerSettings.weHaveInformation;
                this.supplierHasInformation = this.product.informationOwnerSettings.supplierHasInformation;
            }
            this.fetchSubstanceLists();
        },
        includedSubstances(substances) {
            this.calculateIncludedSubstancesSelectedState();
        },
        excludedSubstances(substances) {
            this.calculateExcludedSubstancesSelectedState();
        },
        companyHasInformation(has) {
            this.weHaveInformation = has;
        },
        substanceListVisibilityContextKey() {
            this.refreshSubstanceListsForVisibilityContext();
        },
        legislations(currentLegislations, previousLegislations) {
            if (currentLegislations.length > 0 && 0 === previousLegislations.length) {
                this.fetchSubstanceLists();
            }
        }
    },

    created() {
        this.fetchSubstanceLists().then(() => {
            this.$nextTick(() => {
                this.updateStateForAllLists();
            });
        });
    },
}
</script>
